import { inject, Injectable } from "@angular/core";
import { UpdateSmeSelectionResultsSummaryPwc, UpdateSmeSelectionResultSw } from "./eea-output-project.interface";
import { EEAOutputProjectApiService } from "./eea-output-project.api.service";
import { lastValueFrom, take } from "rxjs";
import { RowSelectedEvent } from "ag-grid-community";
import { GridComponent } from "src/app/shared/components/grid/grid.component";
import { Utils } from "src/app/shared/utils/utils";
import { Constants } from "src/app/shared/utils/constants";

@Injectable({
  providedIn: 'root'
})

export class EEAOutputProjectLogicService {

  private apiService = inject(EEAOutputProjectApiService);
  public async updateSmeSelectionResultsSw(smeSelections: UpdateSmeSelectionResultSw[]): Promise<boolean> {
    const source$ = this.apiService.updateSmeSelectionResultsSw(smeSelections).pipe(take(1));
    const result = await lastValueFrom(source$);
    return result;
  }

  public setSmeSelectionResultsSw( event: RowSelectedEvent ){
    const { node } = event;
    const { data, selected } = node as any;
    const smeSelection: UpdateSmeSelectionResultSw ={
      resultSurfaceWaterPk: this.getResultSurfaceWaterPk( data ),
      smeSelectionValue: selected,
    }
    this.updateSmeSelectionResultsSw( [smeSelection] );
  }

  public setSmeSelectionResultSummaryPwc( event: RowSelectedEvent ){
    const { node } = event;
    const { data, selected } = node as any;
    const { ResultsSummaryPK } = data;
    const smeSelection: UpdateSmeSelectionResultsSummaryPwc ={
      ResultsSummaryPK: ResultsSummaryPK,
      smeSelectionValue: selected,
    }
    this.updateSmeSelectionResultSummaryPwc( [smeSelection] );
  }

  public async updateSmeSelectionResultSummaryPwc(smeSelections: UpdateSmeSelectionResultsSummaryPwc[]): Promise<boolean> {
    const source$ = this.apiService.updateSmeSelectionResultSummaryPwc(smeSelections).pipe(take(1));
    const result = await lastValueFrom(source$);
    return result;
  }

  public setSmeSelectionsResultsSw( grid: GridComponent ): void {
    const smeSelections: UpdateSmeSelectionResultSw[] = [];

    grid.gridApi.forEachNode((node: any) => {
      const { data, selected} = node;
      smeSelections.push({
        resultSurfaceWaterPk: this.getResultSurfaceWaterPk( data ),
        smeSelectionValue: selected
      });
    });

    this.updateSmeSelectionResultsSw( smeSelections );
  }

  public setSmeSelectionsResultSummaryPwc( grid: GridComponent ): void {
    const smeSelections: UpdateSmeSelectionResultsSummaryPwc[] = [];

    grid.gridApi.forEachNode((node: any) => {
      const { data, selected} = node;
      smeSelections.push({
        ResultsSummaryPK: data.ResultsSummaryPK,
        smeSelectionValue: selected
      });
    });

    this.updateSmeSelectionResultSummaryPwc( smeSelections );
  }



  public async signOffSmeSelectionsResultsSw( grid: GridComponent ): Promise<void> {
    const smeSelections = this.getMaxRelatedSmeSelectionsResultsSw( grid );
    const result = await this.updateSmeSelectionResultsSw( smeSelections );
    if ( result ) {
      Utils.showSuccessMessage( Constants.MESSAGE_SAVED_SUCCESSFULLY );
    }
  }

 
  private getMaxRelatedSmeSelectionsResultsSw( grid: GridComponent ): UpdateSmeSelectionResultSw[] {
    const leafNodesByGroup = new Map<string, any[]>();
    const pivotFields = this.getPivotFields( grid );

    grid.gridApi.forEachNode((node: any) => {
      if ( !node || node.group || !node.data ) return;

      const groupKey = `${this.getRowGroupKey( node )}|${this.getPivotValuesKey( node, pivotFields )}`;
      const nodes = leafNodesByGroup.get( groupKey ) ?? [];
      nodes.push( node );
      leafNodesByGroup.set( groupKey, nodes );
    });

    const smeSelections: UpdateSmeSelectionResultSw[] = [];

    leafNodesByGroup.forEach((nodes) => {
      const maxNode = this.getMaxRelatedNode( nodes, 'MaxPecSw' );
      if ( !maxNode ) return;

      nodes.forEach((node) => {
        smeSelections.push({
          resultSurfaceWaterPk: this.getResultSurfaceWaterPk( node.data ),
          smeSelectionValue: node === maxNode
        });
      });
    });

    return smeSelections;
  }

  private getResultSurfaceWaterPk( data: any ): number {
    return data.ResultsSurfaceWaterPk ?? data.resultsSurfaceWaterPk;
  }

  private getPivotFields( grid: GridComponent ): string[] {
    const pivotColumns = grid.gridColumnApi?.getPivotColumns?.() ?? [];
    return pivotColumns
      .map((col: any) => col.getColDef?.()?.field)
      .filter((field: any): field is string => !!field);
  }

  private getPivotValuesKey( node: any, pivotFields: string[] ): string {
    return pivotFields
      .map((field) => node.data?.[field] != null ? String( node.data[field] ).trim().toLowerCase() : '')
      .join( '|' );
  }

  private getRowGroupKey( node: any ): string {
    const keys: string[] = [];
    let current = node.parent;
    while ( current && current.level >= 0 ) {
      keys.unshift( current.key != null ? String( current.key ).trim().toLowerCase() : '' );
      current = current.parent;
    }
    return keys.join( '|' );
  }

  private getMaxRelatedNode( nodes: any[], valueField: string ): any | null {
    return nodes.reduce((max: any, current: any) => {
      if ( !max ) return current;

      const maxValue = Number( max.data?.[valueField] );
      const currentValue = Number( current.data?.[valueField] );

      if ( isNaN( currentValue ) ) return max;
      if ( isNaN( maxValue ) ) return current;

      return currentValue > maxValue ? current : max;
    }, null);
  }

}
